import { formatMoroccoLocation, getMoroccoName } from "../../../shared/lib/locale";
import { activityHrefString } from "../lib/activity-href";
import { usesEditorialHubCopy } from "../lib/explore-segments";
import { HUB_COPY } from "./hub-copy";

type Lang = "en" | "fr" | "ar" | "es" | "it" | "de" | "nl";

interface Templates {
  kicker: {
    city: (city: string) => string;
    none: (country: string) => string;
  };
  h1: {
    cityCat: (cat: string, city: string) => string;
    cat: (cat: string, country: string) => string;
    city: (city: string) => string;
    none: (country: string) => string;
  };
  title: {
    city: (location: string, year: number) => string;
    none: (country: string, year: number) => string;
  };
  description: {
    city: (city: string) => string;
    none: () => string;
  };
  lede: {
    city: (city: string, tagline?: string) => string;
    none: (country: string) => string;
  };
  sections: {
    about: (city: string, vibe: string, reason: string, tagline: string) => { title: string; body: string };
    whyCity: (city: string, reason: string) => { title: string; body: string };
  };
  faq: {
    city: (city: string, neighborhoods?: string[], bestTime?: { window: string; note: string }) => Array<{ q: string; a: string }>;
  };
  joiner: { and: string };
}

const LIST_JOINERS: Record<Lang, { sep: string; last: string; pair: string }> = {
  en: { sep: ", ", last: ", and ", pair: " and " },
  fr: { sep: ", ", last: " et ", pair: " et " },
  ar: { sep: "، ", last: "، و", pair: " و" },
  es: { sep: ", ", last: " y ", pair: " y " },
  it: { sep: ", ", last: " e ", pair: " e " },
  de: { sep: ", ", last: " und ", pair: " und " },
  nl: { sep: ", ", last: " en ", pair: " en " },
};

function joinList(items: string[], lang: Lang): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  const { sep, last, pair } = LIST_JOINERS[lang];
  // Two items take no serial comma: "Marrakech and Rabat".
  if (items.length === 2) return `${items[0]}${pair}${items[1]}`;
  return `${items.slice(0, -1).join(sep)}${last}${items[items.length - 1]}`;
}

const STRINGS: Record<Lang, Templates> = {
  en: {
    kicker: {
      city: (city) => `What to do in ${city}`,
      none: (country) => `Explore ${country}`,
    },
    h1: {
      cityCat: (cat, city) => `${cat} in ${city}`,
      cat: (cat, country) => `${cat} in ${country}`,
      city: (city) => `Things to do in ${city}`,
      none: (country) => `Explore ${country}`,
    },
    title: {
      city: (loc, y) => `Best Things to Do in ${loc} (${y}) | MaJourneys`,
      none: (country, y) => `Explore ${country} (${y}) · Tours, Stays & Experiences | MaJourneys`,
    },
    description: {
      city: (city) =>
        `Discover ${city}'s best tours, stays, and experiences, vetted by our local team. Reserve free, pay on arrival.`,
      none: () => "Handpicked Moroccan experiences, tours, stays, spas, workshops. Reserve free, pay on arrival.",
    },
    lede: {
      city: (city, tagline) =>
        tagline
          ? `${tagline} These are the ${city} experiences locals actually recommend, sorted by category so you can plan your day in one glance.`
          : `Discover the ${city} experiences locals actually recommend, sorted by category so you can plan your day in one glance.`,
      none: (country) =>
        `MaJourneys handpicks the experiences worth your time across ${country}. Compare, book, and go, with local guides and honest prices.`,
    },
    sections: {
      about: (city, vibe, reason, tagline) => ({
        title: `About ${city}`,
        body: `${tagline} Expect ${vibe}. ${reason}`,
      }),
      whyCity: (city, reason) => ({
        title: `Why ${city}`,
        body: reason,
      }),
    },
    faq: {
      city: (city, neighborhoods, bestTime) => [
        { q: `What's the best way to explore ${city}?`, a: `Mix a guided tour with a free-roam day. Our ${city} picks cover culture, food, adventure, and wellness, choose a category above to focus your plan.` },
        { q: `When is the best time to visit ${city}?`, a: bestTime ? `${bestTime.window}. ${bestTime.note}` : "Spring (March to May) and fall (September to November) are the most comfortable." },
        { q: `Where should I stay in ${city}?`, a: neighborhoods && neighborhoods.length ? `Popular areas: ${joinList(neighborhoods.slice(0, 3), "en")}. Each has its own vibe, medina for atmosphere, newer districts for comfort.` : "Central districts are usually the best mix of atmosphere and convenience." },
      ],
    },
    joiner: { and: "and" },
  },
  fr: {
    kicker: {
      city: (city) => `Que faire à ${city}`,
      none: (country) => `Explorer le ${country}`,
    },
    h1: {
      cityCat: (cat, city) => `${cat} à ${city}`,
      cat: (cat, country) => `${cat} au ${country}`,
      city: (city) => `Que faire à ${city}`,
      none: (country) => `Explorer le ${country}`,
    },
    title: {
      city: (loc, y) => `Que faire à ${loc} (${y}) | MaJourneys`,
      none: (country, y) => `Explorer le ${country} (${y}) · Tours, séjours & expériences | MaJourneys`,
    },
    description: {
      city: (city) =>
        `Découvrez les meilleurs tours, séjours et expériences à ${city}, sélectionnés par notre équipe locale. Réservation gratuite, paiement sur place.`,
      none: () =>
        "Expériences marocaines triées sur le volet, tours, séjours, spas, ateliers. Réservation gratuite, paiement sur place.",
    },
    lede: {
      city: (city, tagline) =>
        tagline
          ? `${tagline} Voici les expériences à ${city} que les locaux recommandent vraiment, classées par catégorie pour planifier votre journée d'un coup d'œil.`
          : `Voici les expériences à ${city} que les locaux recommandent vraiment, classées par catégorie pour planifier votre journée d'un coup d'œil.`,
      none: (country) =>
        `MaJourneys sélectionne les expériences qui valent vraiment votre temps au ${country}. Comparez, réservez, partez, avec des guides locaux et des prix honnêtes.`,
    },
    sections: {
      about: (city, vibe, reason, tagline) => ({
        title: `À propos de ${city}`,
        body: `${tagline} Attendez-vous à ${vibe}. ${reason}`,
      }),
      whyCity: (city, reason) => ({
        title: `Pourquoi ${city}`,
        body: reason,
      }),
    },
    faq: {
      city: (city, neighborhoods, bestTime) => [
        { q: `Quelle est la meilleure façon d'explorer ${city} ?`, a: `Combinez un tour guidé avec une journée libre. Nos sélections à ${city} couvrent culture, gastronomie, aventure et bien-être, choisissez une catégorie pour cibler votre plan.` },
        { q: `Quelle est la meilleure période pour visiter ${city} ?`, a: bestTime ? `${bestTime.window}. ${bestTime.note}` : "Le printemps (de mars à mai) et l'automne (de septembre à novembre) sont les plus agréables." },
        { q: `Où loger à ${city} ?`, a: neighborhoods && neighborhoods.length ? `Quartiers populaires : ${joinList(neighborhoods.slice(0, 3), "fr")}. Chacun a son ambiance, médina pour l'atmosphère, quartiers récents pour le confort.` : "Les quartiers centraux offrent généralement le meilleur compromis entre atmosphère et confort." },
      ],
    },
    joiner: { and: "et" },
  },
  ar: {
    kicker: {
      city: (city) => `ماذا تفعل في ${city}`,
      none: (country) => `استكشف ${country}`,
    },
    h1: {
      cityCat: (cat, city) => `${cat} في ${city}`,
      cat: (cat, country) => `${cat} في ${country}`,
      city: (city) => `أنشطة في ${city}`,
      none: (country) => `استكشف ${country}`,
    },
    title: {
      city: (loc, y) => `أفضل أنشطة في ${loc} (${y}) | MaJourneys`,
      none: (country, y) => `استكشف ${country} (${y}) · جولات وإقامات وتجارب | MaJourneys`,
    },
    description: {
      city: (city) =>
        `اكتشف أفضل الجولات والإقامات والتجارب في ${city}, مختارة من فريقنا المحلي. احجز مجاناً وادفع عند الوصول.`,
      none: () => "تجارب مغربية مختارة بعناية, جولات وإقامات وسبا وورش. احجز مجاناً وادفع عند الوصول.",
    },
    lede: {
      city: (city, tagline) =>
        tagline
          ? `${tagline} هذه هي تجارب ${city} التي يوصي بها السكان المحليون فعلاً, مرتبة حسب الفئة لتخطيط يومك بنظرة واحدة.`
          : `اكتشف تجارب ${city} التي يوصي بها السكان المحليون فعلاً, مرتبة حسب الفئة لتخطيط يومك بنظرة واحدة.`,
      none: (country) =>
        `يختار MaJourneys التجارب التي تستحق وقتك في ${country}. قارن، احجز، وانطلق, مع مرشدين محليين وأسعار صادقة.`,
    },
    sections: {
      about: (city, vibe, reason, tagline) => ({
        title: `عن ${city}`,
        body: `${tagline} توقّع ${vibe}. ${reason}`,
      }),
      whyCity: (city, reason) => ({
        title: `لماذا ${city}`,
        body: reason,
      }),
    },
    faq: {
      city: (city, neighborhoods, bestTime) => [
        { q: `ما أفضل طريقة لاستكشاف ${city}؟`, a: `امزج جولة موجهة مع يوم حر. اختياراتنا في ${city} تغطي الثقافة والطعام والمغامرة والعافية, اختر فئة أعلاه لتركيز خطتك.` },
        { q: `متى أفضل وقت لزيارة ${city}؟`, a: bestTime ? `${bestTime.window}. ${bestTime.note}` : "الربيع (من مارس إلى ماي) والخريف (من شتنبر إلى نونبر) هما الأكثر راحة." },
        { q: `أين تقيم في ${city}؟`, a: neighborhoods && neighborhoods.length ? `الأحياء الشهيرة: ${joinList(neighborhoods.slice(0, 3), "ar")}. لكل منها طابعها, المدينة القديمة للأجواء، الأحياء الحديثة للراحة.` : "تقدم الأحياء المركزية عادة أفضل مزيج من الأجواء والراحة." },
      ],
    },
    joiner: { and: "و" },
  },
  es: {
    kicker: {
      city: (city) => `Qué hacer en ${city}`,
      none: (country) => `Explora ${country}`,
    },
    h1: {
      cityCat: (cat, city) => `${cat} en ${city}`,
      cat: (cat, country) => `${cat} en ${country}`,
      city: (city) => `Qué hacer en ${city}`,
      none: (country) => `Explora ${country}`,
    },
    title: {
      city: (loc, y) => `Qué hacer en ${loc} (${y}) | MaJourneys`,
      none: (country, y) => `Explora ${country} (${y}) · Tours, estancias y experiencias | MaJourneys`,
    },
    description: {
      city: (city) =>
        `Descubre los mejores tours, estancias y experiencias de ${city}, seleccionados por nuestro equipo local. Reserva gratis y paga al llegar.`,
      none: () =>
        "Experiencias marroquíes seleccionadas a mano, tours, estancias, spas, talleres. Reserva gratis y paga al llegar.",
    },
    lede: {
      city: (city, tagline) =>
        tagline
          ? `${tagline} Estas son las experiencias de ${city} que los locales recomiendan de verdad, ordenadas por categoría para planificar tu día de un vistazo.`
          : `Descubre las experiencias de ${city} que los locales recomiendan de verdad, ordenadas por categoría para planificar tu día de un vistazo.`,
      none: (country) =>
        `MaJourneys selecciona las experiencias que de verdad merecen tu tiempo en ${country}. Compara, reserva y disfruta, con guías locales y precios honestos.`,
    },
    sections: {
      about: (city, vibe, reason, tagline) => ({
        title: `Sobre ${city}`,
        body: `${tagline} Te espera ${vibe}. ${reason}`,
      }),
      whyCity: (city, reason) => ({
        title: `Por qué ${city}`,
        body: reason,
      }),
    },
    faq: {
      city: (city, neighborhoods, bestTime) => [
        { q: `¿Cuál es la mejor forma de descubrir ${city}?`, a: `Combina un tour guiado con un día por libre. Nuestra selección de ${city} cubre cultura, gastronomía, aventura y bienestar: elige una categoría arriba para centrar tu plan.` },
        { q: `¿Cuándo es la mejor época para visitar ${city}?`, a: bestTime ? `${bestTime.window}. ${bestTime.note}` : "La primavera (de marzo a mayo) y el otoño (de septiembre a noviembre) son las épocas más agradables." },
        { q: `¿Dónde alojarse en ${city}?`, a: neighborhoods && neighborhoods.length ? `Zonas populares: ${joinList(neighborhoods.slice(0, 3), "es")}. Cada una tiene su ambiente: la medina por su atmósfera, los barrios nuevos por su comodidad.` : "Los barrios céntricos suelen ofrecer el mejor equilibrio entre ambiente y comodidad." },
      ],
    },
    joiner: { and: "y" },
  },
  it: {
    kicker: {
      city: (city) => `Cosa fare a ${city}`,
      none: (country) => `Esplora il ${country}`,
    },
    h1: {
      cityCat: (cat, city) => `${cat} a ${city}`,
      cat: (cat, country) => `${cat} in ${country}`,
      city: (city) => `Cosa fare a ${city}`,
      none: (country) => `Esplora il ${country}`,
    },
    title: {
      city: (loc, y) => `Cosa fare a ${loc} (${y}) | MaJourneys`,
      none: (country, y) => `Esplora il ${country} (${y}) · Tour, soggiorni ed esperienze | MaJourneys`,
    },
    description: {
      city: (city) =>
        `Scopri i migliori tour, soggiorni ed esperienze di ${city}, verificati dal nostro team locale. Prenota gratis e paghi all'arrivo.`,
      none: () =>
        "Esperienze marocchine selezionate con cura, tour, soggiorni, spa, laboratori. Prenota gratis e paghi all'arrivo.",
    },
    lede: {
      city: (city, tagline) =>
        tagline
          ? `${tagline} Queste sono le esperienze di ${city} che la gente del posto consiglia davvero, ordinate per categoria per pianificare la giornata a colpo d'occhio.`
          : `Scopri le esperienze di ${city} che la gente del posto consiglia davvero, ordinate per categoria per pianificare la giornata a colpo d'occhio.`,
      none: (country) =>
        `MaJourneys seleziona le esperienze che meritano davvero il tuo tempo in ${country}. Confronta, prenota e parti, con guide locali e prezzi onesti.`,
    },
    sections: {
      about: (city, vibe, reason, tagline) => ({
        title: `Su ${city}`,
        body: `${tagline} Aspettati ${vibe}. ${reason}`,
      }),
      whyCity: (city, reason) => ({
        title: `Perché ${city}`,
        body: reason,
      }),
    },
    faq: {
      city: (city, neighborhoods, bestTime) => [
        { q: `Qual è il modo migliore per esplorare ${city}?`, a: `Combina un tour guidato con una giornata libera. Le nostre proposte per ${city} coprono cultura, cibo, avventura e benessere: scegli una categoria qui sopra per orientare il tuo piano.` },
        { q: `Qual è il periodo migliore per visitare ${city}?`, a: bestTime ? `${bestTime.window}. ${bestTime.note}` : "La primavera (da marzo a maggio) e l'autunno (da settembre a novembre) sono i più piacevoli." },
        { q: `Dove alloggiare a ${city}?`, a: neighborhoods && neighborhoods.length ? `Zone più richieste: ${joinList(neighborhoods.slice(0, 3), "it")}. Ognuna ha il suo carattere: la medina per l'atmosfera, i quartieri nuovi per la comodità.` : "I quartieri centrali offrono di solito il miglior equilibrio tra atmosfera e comodità." },
      ],
    },
    joiner: { and: "e" },
  },
  de: {
    kicker: {
      city: (city) => `Was tun in ${city}`,
      none: (country) => `${country} entdecken`,
    },
    h1: {
      cityCat: (cat, city) => `${cat} in ${city}`,
      cat: (cat, country) => `${cat} in ${country}`,
      city: (city) => `Aktivitäten in ${city}`,
      none: (country) => `${country} entdecken`,
    },
    title: {
      city: (loc, y) => `Top-Aktivitäten in ${loc} (${y}) | MaJourneys`,
      none: (country, y) => `${country} entdecken (${y}) · Touren, Unterkünfte & Erlebnisse | MaJourneys`,
    },
    description: {
      city: (city) =>
        `Entdecken Sie die besten Touren, Unterkünfte und Erlebnisse in ${city}, geprüft von unserem lokalen Team. Kostenlos reservieren, Zahlung vor Ort.`,
      none: () =>
        "Handverlesene marokkanische Erlebnisse, Touren, Unterkünfte, Spas, Workshops. Kostenlos reservieren, Zahlung vor Ort.",
    },
    lede: {
      city: (city, tagline) =>
        tagline
          ? `${tagline} Das sind die Erlebnisse in ${city}, die Einheimische wirklich empfehlen, nach Kategorien sortiert, damit Sie Ihren Tag auf einen Blick planen können.`
          : `Entdecken Sie die Erlebnisse in ${city}, die Einheimische wirklich empfehlen, nach Kategorien sortiert, damit Sie Ihren Tag auf einen Blick planen können.`,
      none: (country) =>
        `MaJourneys wählt die Erlebnisse aus, die Ihre Zeit in ${country} wirklich wert sind. Vergleichen, buchen, losfahren, mit lokalen Guides und ehrlichen Preisen.`,
    },
    sections: {
      about: (city, vibe, reason, tagline) => ({
        title: `Über ${city}`,
        body: `${tagline} Erwarten Sie ${vibe}. ${reason}`,
      }),
      whyCity: (city, reason) => ({
        title: `Warum ${city}`,
        body: reason,
      }),
    },
    faq: {
      city: (city, neighborhoods, bestTime) => [
        { q: `Wie erkundet man ${city} am besten?`, a: `Kombinieren Sie eine geführte Tour mit einem freien Tag. Unsere Auswahl für ${city} umfasst Kultur, Kulinarik, Abenteuer und Wellness, wählen Sie oben eine Kategorie, um Ihren Plan zu fokussieren.` },
        { q: `Wann ist die beste Reisezeit für ${city}?`, a: bestTime ? `${bestTime.window}. ${bestTime.note}` : "Frühling (März bis Mai) und Herbst (September bis November) sind am angenehmsten." },
        { q: `Wo übernachtet man in ${city}?`, a: neighborhoods && neighborhoods.length ? `Beliebte Viertel: ${joinList(neighborhoods.slice(0, 3), "de")}. Jedes hat seinen eigenen Charakter, die Medina für Atmosphäre, neuere Viertel für Komfort.` : "Zentrale Viertel bieten meist die beste Mischung aus Atmosphäre und Komfort." },
      ],
    },
    joiner: { and: "und" },
  },
  nl: {
    kicker: {
      city: (city) => `Wat te doen in ${city}`,
      none: (country) => `Ontdek ${country}`,
    },
    h1: {
      cityCat: (cat, city) => `${cat} in ${city}`,
      cat: (cat, country) => `${cat} in ${country}`,
      city: (city) => `Wat te doen in ${city}`,
      none: (country) => `Ontdek ${country}`,
    },
    title: {
      city: (loc, y) => `Wat te doen in ${loc} (${y}) | MaJourneys`,
      none: (country, y) => `Ontdek ${country} (${y}) · Tours, verblijven & ervaringen | MaJourneys`,
    },
    description: {
      city: (city) =>
        `Ontdek de beste tours, verblijven en ervaringen in ${city}, gecontroleerd door ons lokale team. Reserveer gratis, betaal ter plaatse.`,
      none: () =>
        "Zorgvuldig geselecteerde Marokkaanse ervaringen, tours, verblijven, spa's, workshops. Reserveer gratis, betaal ter plaatse.",
    },
    lede: {
      city: (city, tagline) =>
        tagline
          ? `${tagline} Dit zijn de ervaringen in ${city} die locals echt aanraden, gesorteerd op categorie, zodat je je dag in één oogopslag plant.`
          : `Ontdek de ervaringen in ${city} die locals echt aanraden, gesorteerd op categorie, zodat je je dag in één oogopslag plant.`,
      none: (country) =>
        `MaJourneys selecteert de ervaringen die je tijd in ${country} echt waard zijn. Vergelijk, boek en ga, met lokale gidsen en eerlijke prijzen.`,
    },
    sections: {
      about: (city, vibe, reason, tagline) => ({
        title: `Over ${city}`,
        body: `${tagline} Verwacht ${vibe}. ${reason}`,
      }),
      whyCity: (city, reason) => ({
        title: `Waarom ${city}`,
        body: reason,
      }),
    },
    faq: {
      city: (city, neighborhoods, bestTime) => [
        { q: `Wat is de beste manier om ${city} te ontdekken?`, a: `Combineer een tour met gids met een dag op eigen houtje. Onze keuzes voor ${city} beslaan cultuur, eten, avontuur en wellness, kies hierboven een categorie om je plan aan te scherpen.` },
        { q: `Wat is de beste reistijd voor ${city}?`, a: bestTime ? `${bestTime.window}. ${bestTime.note}` : "De lente (maart tot mei) en de herfst (september tot november) zijn het aangenaamst." },
        { q: `Waar verblijf je het best in ${city}?`, a: neighborhoods && neighborhoods.length ? `Populaire buurten: ${joinList(neighborhoods.slice(0, 3), "nl")}. Elk heeft z'n eigen sfeer, de medina voor de beleving, nieuwere wijken voor comfort.` : "Centrale wijken bieden meestal de beste mix van sfeer en gemak." },
      ],
    },
    joiner: { and: "en" },
  },
};

function pickLang(locale?: string): Lang {
  if (locale === "fr" || locale === "ar" || locale === "es" || locale === "it" || locale === "de" || locale === "nl") {
    return locale;
  }
  return "en";
}

export interface CategorySeoInput {
  categorySlug?: string;
  citySlug?: string;
  resultCount?: number;
  /**
   * 1-based page of the result grid. Pages past the first canonicalise to
   * themselves, not back to page 1: pointing page 2 at page 1 tells Google the
   * two are the same document, and the 81 Marrakech restaurants that only exist
   * on pages 2-8 are then sitting on a URL Google has been told to disregard.
   */
  page?: number;
  origin?: string;
  locale?: string;
  /** DB-resolved per-locale city seo (overrides hardcoded CITY_META). */
  cityOverride?: Partial<{
    tagline: string;
    vibe: string;
    reason: string;
    bestTimeWindow: string;
    bestTimeNote: string;
    neighborhoods: string[];
  }>;
  /** DB-resolved per-locale category seo (overrides CATEGORY_META + STRINGS highlights). */
  categoryOverride?: Partial<{
    blurb: string;
    pluralShort: string;
    verb: string;
    highlights: string[];
  }>;
  /** Localized display name for the category (from categories.translations.<lang>.name). */
  categoryNameOverride?: string;
  /** Localized display name for the city (from cities.translations.<lang>.name or cities.name). */
  cityNameOverride?: string;
  /**
   * Unique editorial intro for the hub (from categories.description /
   * translations.<lang>.description). Replaces the templated lede so hub
   * first-fold copy isn't boilerplate duplicated across every category.
   */
  introOverride?: string;
  /** Hubs with something in them, the only ones the related links may name. */
  relatedInventory?: RelatedInventory;
  /**
   * How many rows on the hub can be reserved through us. Restaurants and
   * nightlife only promise a table when this is above zero (Fez restaurants
   * are all guide-only). Unknown keeps each hub kind's default.
   */
  bookableCount?: number;
}

export interface RelatedInventory {
  /** Top-level categories with places (in the page's city, on a city hub), localized names. */
  categories?: ReadonlyArray<{ slug: string; name: string }>;
  /** Cities with places (in the page's category, on a category hub), localized names. */
  cities?: ReadonlyArray<{ slug: string; name: string }>;
}

export interface SeoTip {
  icon: string;
  title: string;
  body: string;
}

export interface SeoSection {
  title: string;
  body: string;
}

export interface RelatedLink {
  label: string;
  href: string;
  kind: "category" | "city" | "combo";
}

export interface CategorySeo {
  title: string;
  description: string;
  h1: string;
  kicker: string;
  lede: string;
  highlights: string[];
  sections: SeoSection[];
  tips: SeoTip[];
  bestTime: { window: string; note: string } | null;
  neighborhoods: string[];
  faqs: Array<{ q: string; a: string }>;
  related: RelatedLink[];
  canonical: string;
  categoryName: string | null;
  cityName: string | null;
  citySlug: string | null;
  countryName: string;
  locationName: string;
  keywords: string[];
  locale: string;
}

// City centroids used to populate TouristDestination.geo. Coordinates are
// publicly-known centre points (rounded to 4 dp), not API data, so the
// schema stays available even when the cities table isn't loaded.
const CITY_GEO: Record<string, { latitude: number; longitude: number }> = {
  marrakech: { latitude: 31.6295, longitude: -7.9811 },
  casablanca: { latitude: 33.5731, longitude: -7.5898 },
  rabat: { latitude: 34.0209, longitude: -6.8416 },
  fez: { latitude: 34.0181, longitude: -5.0078 },
  fes: { latitude: 34.0181, longitude: -5.0078 },
  agadir: { latitude: 30.4278, longitude: -9.5981 },
  essaouira: { latitude: 31.5085, longitude: -9.7595 },
  agafay: { latitude: 31.5167, longitude: -8.2833 },
  dakhla: { latitude: 23.6848, longitude: -15.957 },
  tangier: { latitude: 35.7595, longitude: -5.834 },
  tetouan: { latitude: 35.5785, longitude: -5.3684 },
  ouarzazate: { latitude: 30.9189, longitude: -6.8934 },
  chefchaouen: { latitude: 35.1689, longitude: -5.2636 },
  merzouga: { latitude: 31.0991, longitude: -4.0123 },
  taghazout: { latitude: 30.5448, longitude: -9.7095 },
};

function titleize(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

interface CategoryMeta {
  blurb: string;
  pluralShort: string;
  verb: string;
  icon: string;
}

/**
 * Category meta is sourced from the DB via `categoryOverride` (admin → category
 * row's `translations` jsonb). When missing, we render a slug-derived placeholder
 * so the page still composes, but the right answer is to seed/edit the category
 * in admin so editorial copy lands here.
 */
function fallbackCatMeta(slug?: string): CategoryMeta {
  if (!slug) {
    return { blurb: "experiences", pluralShort: "experiences", verb: "try", icon: "ri-sparkling-2-line" };
  }
  const titled = titleize(slug).toLowerCase();
  return { blurb: titled, pluralShort: titled, verb: "try", icon: "ri-sparkling-2-line" };
}

interface CityMeta {
  tagline: string;
  vibe: string;
  bestTime: { window: string; note: string };
  neighborhoods: string[];
  reason: string;
}


/**
 * What a hub sells decides what its copy may promise.
 *
 * - `activity`: bookable tours and experiences, pay on arrival.
 * - `stay`: stays and camps, paid online (their own copy below).
 * - `restaurant`: tables are free to reserve where a venue is bookable, and
 *   there is never a price. Tour FAQs (hotel pickup, guides, free
 *   cancellation, "how much do restaurants cost") read as nonsense here.
 * - `nightlife`: bars and clubs; some take table reservations.
 * - `explore`: attractions, beaches, golf, guides: nothing is sold.
 */
type HubKind = "activity" | "stay" | "restaurant" | "nightlife" | "explore";

const NIGHTLIFE_SLUGS = new Set(["nightlife", "nightclubs"]);

export function hubKindOf(categorySlug: string | undefined): HubKind {
  if (!categorySlug) return "activity";
  if (categorySlug === "stays" || categorySlug === "camp") return "stay";
  if (categorySlug === "restaurants") return "restaurant";
  if (NIGHTLIFE_SLUGS.has(categorySlug)) return "nightlife";
  if (usesEditorialHubCopy(categorySlug)) return "explore";
  return "activity";
}

interface HubContext {
  lang: Lang;
  kind: HubKind;
  /** At least one row on the hub can be reserved through us. */
  bookable: boolean;
  /** A category hub with nothing in it: no praise for an empty shelf. */
  empty: boolean;
  categoryName: string | null;
  cityName: string | null;
  cityMeta: CityMeta | null;
  countryName: string;
  /** Localised names of the cities that stock this category (country hubs). */
  stockedCityNames: string[];
}

function whereOf(ctx: HubContext): string {
  const H = HUB_COPY[ctx.lang];
  return ctx.cityName ? H.inCity(ctx.cityName) : H.inCountry(ctx.countryName);
}

function bestTimeOf(meta: CityMeta | null): { window: string; note: string } | undefined {
  return meta?.bestTime.window ? meta.bestTime : undefined;
}

function buildTips(ctx: HubContext): SeoTip[] {
  const H = HUB_COPY[ctx.lang].tips;
  if (ctx.categoryName && ctx.empty) return [];
  if (ctx.kind === "restaurant" || ctx.kind === "nightlife" || ctx.kind === "explore") {
    return [{ icon: "ri-customer-service-2-line", ...H.exploreMessage }];
  }
  const tips: SeoTip[] = [
    { icon: "ri-time-line", ...H.early(ctx.cityName ? HUB_COPY[ctx.lang].inCity(ctx.cityName) : HUB_COPY[ctx.lang].inCountry(ctx.countryName)) },
    { icon: "ri-wallet-3-line", ...H.included },
    { icon: "ri-customer-service-2-line", ...H.message },
  ];
  // Stays and camps: cancellation terms come from the property, not from us.
  if (ctx.kind !== "stay") tips.push({ icon: "ri-shield-check-line", ...H.cancel });
  return tips;
}

function buildSections(ctx: HubContext): SeoSection[] {
  const T = STRINGS[ctx.lang];
  const H = HUB_COPY[ctx.lang];
  const { cityName, cityMeta, categoryName } = ctx;
  const sections: SeoSection[] = [];

  if (cityName && cityMeta && (cityMeta.vibe || cityMeta.reason)) {
    const about = T.sections.about(cityName, cityMeta.vibe, cityMeta.reason, cityMeta.tagline);
    sections.push({ title: about.title, body: about.body.trim() });
  }

  // Stays and camps: "Why book" talks about vetting operators and guide
  // quality, which reads as nonsense on a hotel hub, so stays keep only the
  // city sections. A page with no category does the same.
  if (ctx.kind === "stay" || !categoryName) {
    if (cityName && cityMeta?.reason) sections.push(T.sections.whyCity(cityName, cityMeta.reason));
    return sections;
  }
  if (ctx.empty) return sections;

  const where = whereOf(ctx);
  switch (ctx.kind) {
    case "restaurant":
      if (ctx.bookable) sections.push(H.restaurant.reserve(where));
      sections.push(H.restaurant.expect);
      break;
    case "nightlife":
      if (ctx.bookable) sections.push(H.nightlife.reserve(where));
      sections.push(H.nightlife.expect);
      break;
    case "explore":
      break;
    default:
      sections.push(
        cityName ? H.sections.whyBookCityCat(categoryName, cityName) : H.sections.whyBookCat(categoryName, ctx.countryName),
      );
      sections.push(H.sections.whatToExpect);
  }
  return sections;
}

function buildFaqs(ctx: HubContext): Array<{ q: string; a: string }> {
  const T = STRINGS[ctx.lang];
  const H = HUB_COPY[ctx.lang];
  const { cityName, cityMeta, categoryName } = ctx;
  if (!categoryName) {
    return cityName ? T.faq.city(cityName, cityMeta?.neighborhoods, bestTimeOf(cityMeta)) : [];
  }
  const where = whereOf(ctx);
  const best = cityName ? [H.faq.bestTime(cityName, bestTimeOf(cityMeta))] : [];
  if (ctx.empty) return best;

  switch (ctx.kind) {
    case "restaurant":
      return [
        ...(ctx.bookable ? [H.restaurant.freeToReserve, H.restaurant.bookingTimes] : [H.restaurant.notBookable(where)]),
        H.restaurant.menu,
        H.restaurant.dress,
        ...best,
      ];
    case "nightlife":
      return [
        H.nightlife.days,
        ctx.bookable ? H.nightlife.tables : H.nightlife.notBookable(where),
        H.nightlife.dress,
        ...best,
      ];
    case "explore":
      return best;
    case "stay":
      // Camps (stays have their own set). No free-cancellation promise:
      // the terms are the camp's.
      return [H.faq.pick(categoryName, where), H.faq.price(categoryName, where), H.faq.advance(where), ...best];
    default:
      if (cityName) {
        return [
          H.faq.pick(categoryName, where),
          H.faq.price(categoryName, where),
          H.faq.cancel,
          H.faq.advance(where),
          ...best,
          H.faq.pickup(where),
        ];
      }
      return [
        // Only cities the listing really has: a fixed "Marrakech, Casablanca,
        // Agadir and Fes lead" was false for most categories.
        ...(ctx.stockedCityNames.length
          ? [H.faq.whichCities(categoryName, joinList(ctx.stockedCityNames.slice(0, 6), ctx.lang))]
          : []),
        H.faq.family(categoryName),
        H.faq.guide,
        H.faq.howToBook,
      ];
  }
}

/**
 * Cross-links under the hub. Each one points at a hub the page's own data says
 * holds at least one place (`relatedInventory`), in the page's language: a
 * link to an empty hub is a link to a `noindex` page, and these used to be
 * fixed `/en/` combos (Pools in Fez, Excursions in Tangier) whatever was in
 * them. With no inventory passed, nothing is linked rather than guessed.
 */
function buildRelated(
  lang: Lang,
  categorySlug: string | undefined,
  citySlug: string | undefined,
  categoryName: string | null,
  cityName: string | null,
  inventory: RelatedInventory | undefined,
): RelatedLink[] {
  const T = STRINGS[lang];
  const related: RelatedLink[] = [];
  const otherCats = (inventory?.categories ?? []).filter((c) => c.slug !== categorySlug);
  const otherCities = (inventory?.cities ?? []).filter((c) => c.slug !== citySlug);

  if (citySlug && cityName) {
    for (const c of otherCats.slice(0, 4)) {
      related.push({
        label: T.h1.cityCat(c.name, cityName),
        href: `/${lang}/category/${c.slug}/${citySlug}`,
        kind: "combo",
      });
    }
  } else if (categorySlug && categoryName) {
    for (const city of otherCities.slice(0, 4)) {
      related.push({
        label: T.h1.cityCat(categoryName, city.name),
        href: `/${lang}/category/${categorySlug}/${city.slug}`,
        kind: "combo",
      });
    }
  } else if (!categorySlug && !citySlug) {
    for (const c of otherCats.slice(0, 3)) {
      related.push({ label: c.name, href: `/${lang}/category/${c.slug}`, kind: "category" });
    }
    for (const city of otherCities.slice(0, 3)) {
      related.push({ label: city.name, href: `/${lang}/place/${city.slug}`, kind: "city" });
    }
  }

  return related.slice(0, 8);
}

/**
 * Stays are the one category paid ONLINE, in full, at booking time (LiteAPI).
 * Every generic template above ends on the pay-on-arrival promise, which is a
 * false claim on stays hubs, so stays pages swap in these strings instead.
 * Cancellation terms come from the property, not from us, hence the wording.
 */
const PAY_TAG: Record<Lang, string> = {
  en: "No Prepayment",
  fr: "Sans prépaiement",
  ar: "بدون دفع مسبق",
  es: "Sin prepago",
  it: "Senza prepagamento",
  de: "Keine Vorauszahlung",
  nl: "Geen vooruitbetaling",
};

/**
 * Desert camps (`/category/camp`) are stays, and every stay is paid online
 * (owner, 2026-09-25). A camp is reserved through the booking form, then our
 * team emails a secure payment link for the full amount. The generic
 * "Reserve free, pay on arrival" closer and the "No Prepayment" title tag are
 * false here, so the camp hub swaps them for these.
 */
const CAMP_SEO: Record<
  Lang,
  {
    tag: string;
    description: (city: string | null, country: string) => string;
    payFaq: { q: string; a: string };
  }
> = {
  en: {
    tag: "Paid Online",
    description: (city, country) =>
      city
        ? `Compare camps in ${city}: real photos and honest prices. Reserve here, then pay online in full from a secure payment link we email you.`
        : `Find the best camps across ${country}, handpicked by our local team. Reserve here, then pay online in full from a secure payment link we email you.`,
    payFaq: {
      q: "How do I pay for a camp?",
      a: "Camps are paid online, in full. Reserve through the form on the camp's page, then our team emails you a secure payment link for the full amount. Your place is held once it is paid.",
    },
  },
  fr: {
    tag: "Paiement en ligne",
    description: (city, country) =>
      city
        ? `Comparez les camps à ${city} : vraies photos et prix honnêtes. Réservez ici, puis payez en ligne la totalité via le lien de paiement sécurisé que nous vous envoyons par e-mail.`
        : `Trouvez les meilleurs camps au ${country}, sélectionnés par notre équipe locale. Réservez ici, puis payez en ligne la totalité via le lien de paiement sécurisé que nous vous envoyons par e-mail.`,
    payFaq: {
      q: "Comment payer un camp ?",
      a: "Les camps se paient en ligne, en totalité. Réservez via le formulaire de la page du camp, puis notre équipe vous envoie par e-mail un lien de paiement sécurisé pour le montant total. Votre place est retenue dès le paiement.",
    },
  },
  ar: {
    tag: "الدفع عبر الإنترنت",
    description: (city, country) =>
      city
        ? `قارن المخيمات في ${city}: صور حقيقية وأسعار واضحة. احجز هنا، ثم ادفع المبلغ كاملاً عبر الإنترنت من خلال رابط دفع آمن نرسله إليك بالبريد الإلكتروني.`
        : `اكتشف أفضل المخيمات في ${country}، اختارها فريقنا المحلي. احجز هنا، ثم ادفع المبلغ كاملاً عبر الإنترنت من خلال رابط دفع آمن نرسله إليك بالبريد الإلكتروني.`,
    payFaq: {
      q: "كيف أدفع ثمن الإقامة في المخيم؟",
      a: "يُدفع ثمن المخيمات كاملاً عبر الإنترنت. احجز من خلال النموذج في صفحة المخيم، ثم يرسل لك فريقنا رابط دفع آمناً بالمبلغ الكامل عبر البريد الإلكتروني. ويُحجز مكانك بمجرد الدفع.",
    },
  },
  es: {
    tag: "Pago en línea",
    description: (city, country) =>
      city
        ? `Compara campamentos en ${city}: fotos reales y precios honestos. Reserva aquí y paga en línea el importe total con el enlace de pago seguro que te enviamos por correo.`
        : `Encuentra los mejores campamentos de ${country}, elegidos por nuestro equipo local. Reserva aquí y paga en línea el importe total con el enlace de pago seguro que te enviamos por correo.`,
    payFaq: {
      q: "¿Cómo se paga un campamento?",
      a: "Los campamentos se pagan en línea, por el importe total. Reserva con el formulario de la página del campamento y nuestro equipo te enviará por correo un enlace de pago seguro por el importe total. Tu plaza queda reservada en cuanto se pague.",
    },
  },
  it: {
    tag: "Pagamento online",
    description: (city, country) =>
      city
        ? `Confronta i campi tendati a ${city}: foto reali e prezzi onesti. Prenota qui, poi paga online l'intero importo con il link di pagamento sicuro che ti inviamo via e-mail.`
        : `Trova i migliori campi tendati in ${country}, scelti dal nostro team locale. Prenota qui, poi paga online l'intero importo con il link di pagamento sicuro che ti inviamo via e-mail.`,
    payFaq: {
      q: "Come si paga un campo tendato?",
      a: "I campi tendati si pagano online, per l'intero importo. Prenota con il modulo nella pagina del campo, poi il nostro team ti invia via e-mail un link di pagamento sicuro per l'importo totale. Il tuo posto è riservato una volta effettuato il pagamento.",
    },
  },
  de: {
    tag: "Online bezahlt",
    description: (city, country) =>
      city
        ? `Vergleichen Sie Camps in ${city}: echte Fotos, faire Preise. Hier reservieren und den Gesamtbetrag online über den sicheren Zahlungslink bezahlen, den wir Ihnen per E-Mail senden.`
        : `Die besten Camps in ${country}, ausgewählt von unserem Team vor Ort. Hier reservieren und den Gesamtbetrag online über den sicheren Zahlungslink bezahlen, den wir Ihnen per E-Mail senden.`,
    payFaq: {
      q: "Wie bezahle ich ein Camp?",
      a: "Camps werden online in voller Höhe bezahlt. Reservieren Sie über das Formular auf der Seite des Camps, danach sendet Ihnen unser Team per E-Mail einen sicheren Zahlungslink über den Gesamtbetrag. Ihr Platz ist reserviert, sobald bezahlt wurde.",
    },
  },
  nl: {
    tag: "Online betaald",
    description: (city, country) =>
      city
        ? `Vergelijk kampen in ${city}: echte foto's en eerlijke prijzen. Reserveer hier en betaal het volledige bedrag online via de beveiligde betaallink die we je mailen.`
        : `De beste kampen in ${country}, gekozen door ons lokale team. Reserveer hier en betaal het volledige bedrag online via de beveiligde betaallink die we je mailen.`,
    payFaq: {
      q: "Hoe betaal ik een kamp?",
      a: "Kampen worden online betaald, het volledige bedrag. Reserveer via het formulier op de pagina van het kamp, daarna mailt ons team je een beveiligde betaallink voor het volledige bedrag. Je plek is gereserveerd zodra er is betaald.",
    },
  },
};

const STAYS_SEO: Record<
  Lang,
  {
    tag: string;
    description: (city: string | null, country: string) => string;
    lede: (city: string | null, country: string) => string;
    faqs: (city: string | null, country: string) => Array<{ q: string; a: string }>;
    /** Replaces the activity-worded "What to expect" (hours, guides, gear). */
    whatToExpect: (city: string | null, country: string) => { title: string; body: string };
  }
> = {
  en: {
    tag: "Book Online",
    whatToExpect: (cityName, country) => {
      const city = cityName ?? country;
      return { title: "What to expect", body: `Stays in ${city} range from traditional riads to hotels, resorts and villas. Each listing shows its rooms, its amenities and roughly how far it is from the main sights; pick your dates to see live prices. Stays are paid online when you book, and the check-in time and cancellation terms come from each property, shown before you pay.` };
    },
    description: (city, country) =>
      city
        ? `Compare the best stays in ${city}: real photos, honest prices, instant confirmation. Booked and paid online; cancellation terms are set by each property.`
        : `Find the best stays across ${country}: riads, villas and hotels handpicked by our local team. Book online with instant confirmation.`,
    lede: (city, country) =>
      `Looking for a place to stay in ${city ?? country}? We've curated the riads, villas and hotels worth your night: real photos, honest prices, instant confirmation. Stays are booked and paid online; cancellation terms come from each property.`,
    faqs: (city, country) => [
      { q: "How do I pay for a stay?", a: "Stays are paid online in full when you book, with instant confirmation. Unlike our activities, there is nothing to settle on arrival: your room is secured the moment you pay." },
      { q: "Can I cancel a stay?", a: "Cancellation terms are set by each property and shown clearly before you pay. Many rates cancel free within a set window; some discounted rates are non-refundable." },
      { q: "Do I need to book in advance?", a: `Popular riads and villas in ${city ?? country} sell out early in peak season. Book as soon as your dates are fixed.` },
    ],
  },
  fr: {
    tag: "Réservation en ligne",
    whatToExpect: (cityName, country) => {
      const city = cityName ?? country;
      return { title: "À quoi s'attendre", body: `À ${city}, les hébergements vont des riads traditionnels aux hôtels, resorts et villas. Chaque fiche présente les chambres, les équipements et la distance approximative des principaux sites ; choisissez vos dates pour voir les prix en direct. Les séjours se paient en ligne à la réservation, et l'heure d'arrivée comme les conditions d'annulation sont fixées par chaque établissement et affichées avant le paiement.` };
    },
    description: (city, country) =>
      city
        ? `Comparez les meilleurs séjours à ${city} : photos réelles, prix honnêtes, confirmation immédiate. Réservation et paiement en ligne ; conditions d'annulation fixées par chaque établissement.`
        : `Trouvez les meilleurs séjours au ${country} : riads, villas et hôtels sélectionnés par notre équipe locale. Réservez en ligne avec confirmation immédiate.`,
    lede: (city, country) =>
      `Vous cherchez où dormir ${city ? `à ${city}` : `au ${country}`} ? Nous avons sélectionné les riads, villas et hôtels qui valent la nuit : photos réelles, prix honnêtes, confirmation immédiate. Les séjours se réservent et se paient en ligne ; les conditions d'annulation dépendent de chaque établissement.`,
    faqs: (city, country) => [
      { q: "Comment payer un séjour ?", a: "Les séjours se paient en ligne, en totalité, au moment de la réservation, avec confirmation immédiate. Contrairement à nos activités, il n'y a rien à régler à l'arrivée : votre chambre est garantie dès le paiement." },
      { q: "Puis-je annuler un séjour ?", a: "Les conditions d'annulation sont fixées par chaque établissement et affichées clairement avant le paiement. Beaucoup de tarifs s'annulent gratuitement dans un délai donné ; certains tarifs réduits sont non remboursables." },
      { q: "Faut-il réserver à l'avance ?", a: `Les riads et villas populaires ${city ? `à ${city}` : `au ${country}`} affichent complet tôt en haute saison. Réservez dès que vos dates sont fixées.` },
    ],
  },
  ar: {
    tag: "احجز عبر الإنترنت",
    whatToExpect: (cityName, country) => {
      const city = cityName ?? country;
      return { title: "ماذا تتوقع", body: `تتنوع أماكن الإقامة في ${city} بين الرياضات التقليدية والفنادق والمنتجعات والفيلات. تعرض كل صفحة الغرف والمرافق والمسافة التقريبية إلى أبرز المعالم، واختر تواريخك لرؤية الأسعار المباشرة. يُدفع ثمن الإقامة عبر الإنترنت عند الحجز، ويحدد كل مكان إقامة موعد تسجيل الوصول وشروط الإلغاء، وتظهر قبل الدفع.` };
    },
    description: (city, country) =>
      city
        ? `قارن أفضل أماكن الإقامة في ${city}: صور حقيقية وأسعار صادقة وتأكيد فوري. الحجز والدفع عبر الإنترنت؛ وشروط الإلغاء يحددها كل فندق.`
        : `اعثر على أفضل أماكن الإقامة في ${country}: رياضات وفلل وفنادق مختارة من فريقنا المحلي. احجز عبر الإنترنت مع تأكيد فوري.`,
    lede: (city, country) =>
      `تبحث عن مكان للإقامة في ${city ?? country}؟ اخترنا الرياضات والفلل والفنادق التي تستحق ليلتك: صور حقيقية وأسعار صادقة وتأكيد فوري. تُحجز الإقامات وتُدفع عبر الإنترنت، وشروط الإلغاء يحددها كل فندق.`,
    faqs: (city, country) => [
      { q: "كيف أدفع ثمن الإقامة؟", a: "تُدفع الإقامات بالكامل عبر الإنترنت عند الحجز، مع تأكيد فوري. وعلى خلاف أنشطتنا، لا يوجد ما تدفعه عند الوصول: غرفتك مضمونة فور إتمام الدفع." },
      { q: "هل يمكنني إلغاء الإقامة؟", a: "شروط الإلغاء يحددها كل فندق وتظهر بوضوح قبل الدفع. كثير من الأسعار تُلغى مجاناً ضمن مدة محددة، وبعض الأسعار المخفضة غير قابلة للاسترداد." },
      { q: "هل أحتاج إلى الحجز مسبقاً؟", a: `الرياضات والفلل الشهيرة في ${city ?? country} تمتلئ مبكراً في موسم الذروة. احجز فور تحديد مواعيدك.` },
    ],
  },
  es: {
    tag: "Reserva online",
    whatToExpect: (cityName, country) => {
      const city = cityName ?? country;
      return { title: "Qué esperar", body: `En ${city} hay desde riads tradicionales hasta hoteles, resorts y villas. Cada ficha muestra las habitaciones, los servicios y la distancia aproximada a los lugares principales; elige tus fechas para ver los precios en directo. Los alojamientos se pagan en línea al reservar, y la hora de entrada y las condiciones de cancelación las fija cada establecimiento y se muestran antes del pago.` };
    },
    description: (city, country) =>
      city
        ? `Compara las mejores estancias en ${city}: fotos reales, precios honestos, confirmación al instante. Se reservan y pagan online; las condiciones de cancelación las fija cada alojamiento.`
        : `Encuentra las mejores estancias en ${country}: riads, villas y hoteles seleccionados por nuestro equipo local. Reserva online con confirmación al instante.`,
    lede: (city, country) =>
      `¿Buscas dónde dormir en ${city ?? country}? Hemos seleccionado los riads, villas y hoteles que merecen tu noche: fotos reales, precios honestos y confirmación al instante. Las estancias se reservan y pagan online; las condiciones de cancelación dependen de cada alojamiento.`,
    faqs: (city, country) => [
      { q: "¿Cómo pago una estancia?", a: "Las estancias se pagan online, en su totalidad, al reservar, con confirmación al instante. A diferencia de nuestras actividades, no hay nada que abonar a la llegada: tu habitación queda garantizada al pagar." },
      { q: "¿Puedo cancelar una estancia?", a: "Las condiciones de cancelación las fija cada alojamiento y se muestran claramente antes de pagar. Muchas tarifas se cancelan gratis dentro de un plazo; algunas tarifas rebajadas no son reembolsables." },
      { q: "¿Debo reservar con antelación?", a: `Los riads y villas populares de ${city ?? country} se agotan pronto en temporada alta. Reserva en cuanto tengas las fechas.` },
    ],
  },
  it: {
    tag: "Prenota online",
    whatToExpect: (cityName, country) => {
      const city = cityName ?? country;
      return { title: "Cosa aspettarsi", body: `A ${city} si va dai riad tradizionali a hotel, resort e ville. Ogni scheda mostra le camere, i servizi e la distanza indicativa dai luoghi principali; scegli le date per vedere i prezzi in tempo reale. Gli alloggi si pagano online al momento della prenotazione, e l'orario di check-in e le condizioni di cancellazione sono stabiliti da ogni struttura e mostrati prima del pagamento.` };
    },
    description: (city, country) =>
      city
        ? `Confronta i migliori soggiorni a ${city}: foto reali, prezzi onesti, conferma immediata. Si prenotano e pagano online; le condizioni di cancellazione sono stabilite da ogni struttura.`
        : `Trova i migliori soggiorni in ${country}: riad, ville e hotel selezionati dal nostro team locale. Prenota online con conferma immediata.`,
    lede: (city, country) =>
      `Cerchi dove dormire a ${city ?? country}? Abbiamo selezionato i riad, le ville e gli hotel che valgono la tua notte: foto reali, prezzi onesti, conferma immediata. I soggiorni si prenotano e pagano online; le condizioni di cancellazione dipendono da ogni struttura.`,
    faqs: (city, country) => [
      { q: "Come pago un soggiorno?", a: "I soggiorni si pagano online, per intero, al momento della prenotazione, con conferma immediata. A differenza delle nostre attività, non c'è nulla da saldare all'arrivo: la camera è garantita al pagamento." },
      { q: "Posso cancellare un soggiorno?", a: "Le condizioni di cancellazione sono stabilite da ogni struttura e mostrate chiaramente prima del pagamento. Molte tariffe si cancellano gratis entro un certo termine; alcune tariffe scontate non sono rimborsabili." },
      { q: "Devo prenotare in anticipo?", a: `I riad e le ville più richiesti a ${city ?? country} si esauriscono presto in alta stagione. Prenota appena hai le date.` },
    ],
  },
  de: {
    tag: "Online buchen",
    whatToExpect: (cityName, country) => {
      const city = cityName ?? country;
      return { title: "Was Sie erwartet", body: `In ${city} reicht das Angebot von traditionellen Riads bis zu Hotels, Resorts und Villen. Jedes Angebot zeigt die Zimmer, die Ausstattung und die ungefähre Entfernung zu den wichtigsten Sehenswürdigkeiten; wählen Sie Ihre Reisedaten, um aktuelle Preise zu sehen. Unterkünfte werden bei der Buchung online bezahlt, und Check-in-Zeit sowie Stornobedingungen legt jede Unterkunft fest und zeigt sie vor der Zahlung an.` };
    },
    description: (city, country) =>
      city
        ? `Vergleichen Sie die besten Unterkünfte in ${city}: echte Fotos, ehrliche Preise, sofortige Bestätigung. Buchung und Zahlung online; die Stornobedingungen legt jede Unterkunft selbst fest.`
        : `Finden Sie die besten Unterkünfte in ${country}: Riads, Villen und Hotels, ausgewählt von unserem lokalen Team. Online buchen mit sofortiger Bestätigung.`,
    lede: (city, country) =>
      `Sie suchen eine Unterkunft in ${city ?? country}? Wir haben die Riads, Villen und Hotels ausgewählt, die Ihre Nacht wert sind: echte Fotos, ehrliche Preise, sofortige Bestätigung. Unterkünfte werden online gebucht und bezahlt; die Stornobedingungen legt jede Unterkunft selbst fest.`,
    faqs: (city, country) => [
      { q: "Wie bezahle ich eine Unterkunft?", a: "Unterkünfte werden bei der Buchung vollständig online bezahlt, mit sofortiger Bestätigung. Anders als bei unseren Aktivitäten ist bei der Ankunft nichts mehr zu zahlen: Ihr Zimmer ist mit der Zahlung gesichert." },
      { q: "Kann ich eine Unterkunft stornieren?", a: "Die Stornobedingungen legt jede Unterkunft selbst fest; sie werden vor der Zahlung klar angezeigt. Viele Raten sind innerhalb einer Frist kostenlos stornierbar; manche reduzierten Raten sind nicht erstattbar." },
      { q: "Muss ich im Voraus buchen?", a: `Beliebte Riads und Villen in ${city ?? country} sind in der Hochsaison früh ausgebucht. Buchen Sie, sobald Ihre Daten feststehen.` },
    ],
  },
  nl: {
    tag: "Online boeken",
    whatToExpect: (cityName, country) => {
      const city = cityName ?? country;
      return { title: "Wat je kunt verwachten", body: `In ${city} gaat het aanbod van traditionele riads tot hotels, resorts en villa's. Elke pagina toont de kamers, de voorzieningen en ongeveer hoe ver het van de belangrijkste bezienswaardigheden ligt; kies je data om actuele prijzen te zien. Verblijven betaal je online bij het boeken, en de incheck-tijd en annuleringsvoorwaarden bepaalt elke accommodatie zelf; je ziet ze voor je betaalt.` };
    },
    description: (city, country) =>
      city
        ? `Vergelijk de beste verblijven in ${city}: echte foto's, eerlijke prijzen, directe bevestiging. Online geboekt en betaald; annuleringsvoorwaarden worden per accommodatie bepaald.`
        : `Vind de beste verblijven in ${country}: riads, villa's en hotels geselecteerd door ons lokale team. Boek online met directe bevestiging.`,
    lede: (city, country) =>
      `Op zoek naar een verblijf in ${city ?? country}? Wij selecteerden de riads, villa's en hotels die je nacht waard zijn: echte foto's, eerlijke prijzen, directe bevestiging. Verblijven boek en betaal je online; de annuleringsvoorwaarden bepaalt elke accommodatie zelf.`,
    faqs: (city, country) => [
      { q: "Hoe betaal ik een verblijf?", a: "Verblijven betaal je volledig online bij het boeken, met directe bevestiging. Anders dan bij onze activiteiten hoef je bij aankomst niets meer af te rekenen: je kamer is gegarandeerd zodra je betaalt." },
      { q: "Kan ik een verblijf annuleren?", a: "De annuleringsvoorwaarden bepaalt elke accommodatie zelf; ze worden duidelijk getoond voordat je betaalt. Veel tarieven zijn binnen een bepaalde termijn gratis te annuleren; sommige kortingstarieven zijn niet restitueerbaar." },
      { q: "Moet ik vooraf boeken?", a: `Populaire riads en villa's in ${city ?? country} zitten in het hoogseizoen snel vol. Boek zodra je data vaststaan.` },
    ],
  },
};

export function buildCategorySeo(input: CategorySeoInput): CategorySeo {
  const {
    categorySlug,
    citySlug,
    resultCount,
    origin,
    locale,
    cityOverride,
    categoryOverride,
    categoryNameOverride,
    cityNameOverride,
  } = input;
  const lang = pickLang(locale);
  const T = STRINGS[lang];
  const H = HUB_COPY[lang];
  const kind = hubKindOf(categorySlug);
  const editorial = kind === "restaurant" || kind === "nightlife" || kind === "explore";
  // The category name exactly as its locale writes it: never lowercased
  // (German capitalises nouns) and only placed where nothing has to agree
  // with it, see hub-copy.ts.
  const categoryName = categoryNameOverride?.trim()
    ? categoryNameOverride.trim()
    : categorySlug
      ? titleize(categorySlug)
      : null;
  const cityName = cityNameOverride?.trim()
    ? cityNameOverride.trim()
    : citySlug
      ? titleize(citySlug)
      : null;
  const countryName = getMoroccoName(locale);
  const locationName = formatMoroccoLocation(cityName, locale);
  // City + category meta come EXCLUSIVELY from the DB-resolved overrides.
  // When the row is missing or empty, we render with empty strings, admin
  // must seed/edit the city/category to get rich SEO copy. No hardcoded
  // entity data lives in this file anymore.
  const cityM: CityMeta | null = cityOverride
    ? {
        tagline: cityOverride.tagline ?? "",
        vibe: cityOverride.vibe ?? "",
        reason: cityOverride.reason ?? "",
        bestTime: {
          window: cityOverride.bestTimeWindow ?? "",
          note: cityOverride.bestTimeNote ?? "",
        },
        neighborhoods: cityOverride.neighborhoods ?? [],
      }
    : null;
  const count = typeof resultCount === "number" && resultCount > 0 ? resultCount : undefined;
  const empty = !!categoryName && resultCount === 0;
  // Unknown means: assume the honest default for the kind of hub. Bookable
  // activity hubs are bookable; restaurants, nightlife and attractions only
  // when the page says it holds a bookable row.
  const bookable = input.bookableCount !== undefined ? input.bookableCount > 0 : !editorial;
  const fbCat = fallbackCatMeta(categorySlug);
  const cat: CategoryMeta = {
    blurb: categoryOverride?.blurb ?? fbCat.blurb,
    pluralShort: categoryOverride?.pluralShort ?? fbCat.pluralShort,
    verb: categoryOverride?.verb ?? fbCat.verb,
    icon: fbCat.icon,
  };

  const kicker = cityName && categoryName
    ? H.kicker.cityCat(categoryName, locationName)
    : categoryName
      ? H.kicker.cat(categoryName, countryName)
      : cityName
        ? T.kicker.city(cityName)
        : T.kicker.none(countryName);

  const h1 = cityName && categoryName
    ? T.h1.cityCat(categoryName, cityName)
    : categoryName
      ? T.h1.cat(categoryName, countryName)
      : cityName
        ? T.h1.city(cityName)
        : T.h1.none(countryName);

  const year = new Date().getFullYear();
  // Nothing is sold on the editorial hubs, so their title tag cannot say
  // "No Prepayment".
  const noCountTag = editorial ? H.localPicks : PAY_TAG[lang];
  const title = cityName && categoryName
    ? H.title.cityCat(categoryName, locationName, year, count ? H.options(count) : noCountTag)
    : categoryName
      ? H.title.cat(categoryName, countryName, year, noCountTag)
      : cityName
        ? T.title.city(locationName, year)
        : T.title.none(countryName, year);

  const description = cityName && categoryName
    ? editorial
      ? H.description.cityExplore(categoryName, cityName)
      : H.description.cityCat(categoryName, cityName, count)
    : categoryName
      ? editorial
        ? H.description.catExplore(categoryName, countryName)
        : H.description.cat(categoryName, countryName)
      : cityName
        ? T.description.city(cityName)
        : T.description.none();

  const withTagline = (text: string) => (cityM?.tagline ? `${cityM.tagline} ${text}` : text);
  const templatedLede = cityName && categoryName
    ? empty
      // An empty hub keeps the city's own line and says nothing about a
      // category it does not have.
      ? cityM?.tagline ?? ""
      : withTagline(editorial ? H.lede.cityExplore(categoryName, cityName) : H.lede.cityCat(categoryName, cityName))
    : categoryName
      ? editorial
        ? H.lede.catExplore(categoryName, countryName)
        : H.lede.cat(categoryName, countryName)
      : cityName
        ? T.lede.city(cityName, cityM?.tagline)
        : T.lede.none(countryName);
  const lede = input.introOverride?.trim() || templatedLede;

  const highlights = categoryOverride?.highlights && categoryOverride.highlights.length
    ? categoryOverride.highlights
    : [];

  const ctx: HubContext = {
    lang,
    kind,
    bookable,
    empty,
    categoryName,
    cityName,
    cityMeta: cityM,
    countryName,
    stockedCityNames: (input.relatedInventory?.cities ?? []).map((c) => c.name),
  };
  const sections = buildSections(ctx);
  const tips = buildTips(ctx);
  const bestTime = cityM?.bestTime.window ? cityM.bestTime : null;
  const neighborhoods = cityM?.neighborhoods ?? [];
  const related = buildRelated(lang, categorySlug, citySlug, categoryName, cityName, input.relatedInventory);
  const faqs = buildFaqs(ctx);

  // Stays are paid online via LiteAPI, the generic pay-on-arrival promises
  // above are false there. Swap title tag, description, lede, FAQs.
  const isStays = categorySlug === "stays";
  const staysS = isStays ? STAYS_SEO[lang] : null;
  const campS = categorySlug === "camp" ? CAMP_SEO[lang] : null;

  let canonicalPath: string;
  if (categorySlug && citySlug) {
    canonicalPath = `/${lang}/category/${categorySlug}/${citySlug}`;
  } else if (categorySlug) {
    canonicalPath = `/${lang}/category/${categorySlug}`;
  } else if (citySlug) {
    canonicalPath = `/${lang}/place/${citySlug}`;
  } else {
    canonicalPath = `/${lang}/explore`;
  }
  // `?page=1` is never emitted, page 1 already has a clean canonical URL, and
  // a second address for it would split the signal.
  const pageNumber = input.page && input.page > 1 ? input.page : undefined;
  if (pageNumber) canonicalPath = `${canonicalPath}?page=${pageNumber}`;
  const canonical = origin ? `${origin}${canonicalPath}` : canonicalPath;

  const keywords = buildKeywords(categoryName, cityName, cat);

  return {
    title: staysS
      ? title.replace(PAY_TAG[lang], staysS.tag)
      : campS
        ? title.replace(PAY_TAG[lang], campS.tag)
        : title,
    description: staysS
      ? staysS.description(cityName, countryName)
      : campS
        ? campS.description(cityName, countryName)
        : description,
    h1,
    kicker,
    lede: staysS && !input.introOverride?.trim() ? staysS.lede(cityName, countryName) : lede,
    highlights,
    sections,
    tips,
    bestTime,
    neighborhoods,
    faqs: staysS ? staysS.faqs(cityName, countryName) : campS && !empty ? [campS.payFaq, ...faqs] : faqs,
    related,
    canonical,
    categoryName,
    cityName,
    citySlug: citySlug ?? null,
    countryName,
    locationName,
    keywords,
    locale: lang,
  };
}

function buildKeywords(
  categoryName: string | null,
  cityName: string | null,
  cat: CategoryMeta,
): string[] {
  const lc = (s: string) => s.toLowerCase();
  const keywords = new Set<string>();

  if (categoryName && cityName) {
    keywords.add(`${lc(categoryName)} in ${lc(cityName)}`);
    keywords.add(`${lc(categoryName)} ${lc(cityName)}`);
    keywords.add(`best ${lc(categoryName)} in ${lc(cityName)}`);
    keywords.add(`${lc(cityName)} ${lc(categoryName)}`);
    keywords.add(`things to do in ${lc(cityName)}`);
    keywords.add(`${lc(cityName)} morocco`);
    keywords.add(`book ${lc(categoryName)} ${lc(cityName)}`);
    keywords.add(`${cat.pluralShort} ${lc(cityName)}`);
  } else if (categoryName) {
    keywords.add(`${lc(categoryName)} in morocco`);
    keywords.add(`morocco ${lc(categoryName)}`);
    keywords.add(`best ${lc(categoryName)} morocco`);
    keywords.add(cat.pluralShort);
    keywords.add(`book ${lc(categoryName)} morocco`);
  } else if (cityName) {
    keywords.add(`things to do in ${lc(cityName)}`);
    keywords.add(`${lc(cityName)} morocco`);
    keywords.add(`${lc(cityName)} tours`);
    keywords.add(`${lc(cityName)} activities`);
    keywords.add(`visit ${lc(cityName)}`);
  } else {
    keywords.add("morocco tours");
    keywords.add("things to do in morocco");
    keywords.add("morocco activities");
    keywords.add("morocco experiences");
  }

  return Array.from(keywords);
}

function bcp47ForLocale(locale: string): string {
  if (locale === "fr") return "fr-MA";
  if (locale === "ar") return "ar-MA";
  return "en-US";
}

interface JsonLdListItem {
  slug: string;
  title: string;
  images?: string[];
  price?: number;
  rating?: number;
  /** Optional city slug (used to build canonical 4-segment URL). */
  citySlug?: string | null;
  /** Optional category slug (used to build canonical 4-segment URL). */
  categorySlug?: string | null;
  /** Optional explore segment (used to build canonical URL when no category). */
  segment?: string | null;
}

interface JsonLdBuildOptions {
  /** City/category page details from input, used so BreadcrumbList knows what segments apply. */
  categorySlug?: string | null;
  citySlug?: string | null;
}

export function buildJsonLd(
  seo: CategorySeo,
  items: JsonLdListItem[],
  origin: string,
  options?: JsonLdBuildOptions,
) {
  const lang = seo.locale;
  const inLanguage = bcp47ForLocale(lang);
  const itemListId = `${seo.canonical}#itemlist`;
  const breadcrumbId = `${seo.canonical}#breadcrumb`;

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": itemListId,
    name: seo.h1,
    description: seo.description,
    inLanguage,
    numberOfItems: items.length,
    itemListElement: items.slice(0, 20).map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${origin}${activityHrefString({
        lang,
        citySlug: it.citySlug ?? null,
        categorySlug: it.categorySlug ?? null,
        segment: it.segment ?? null,
        slug: it.slug,
      })}`,
      name: it.title,
      image: it.images?.[0],
    })),
  };

  // BreadcrumbList, positions are renumbered contiguously based on which
  // crumbs apply (home → explore → category? → city?). Google invalidates
  // breadcrumbs that skip positions (e.g. 1, 2, 4).
  const crumbs: Array<{ name: string; item: string }> = [
    { name: "Home", item: `${origin}/${lang}` },
    { name: "Explore", item: `${origin}/${lang}/explore` },
  ];
  const canonicalAbsolute = seo.canonical.startsWith("http")
    ? seo.canonical
    : `${origin}${seo.canonical}`;
  if (seo.categoryName) {
    crumbs.push({ name: seo.categoryName, item: canonicalAbsolute });
  }
  if (seo.cityName) {
    crumbs.push({ name: seo.cityName, item: canonicalAbsolute });
  }
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": breadcrumbId,
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.item,
    })),
  };

  const faq = seo.faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        inLanguage,
        mainEntity: seo.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  // TouristDestination.includesAttraction now references the actual indexed
  // items in the list (via canonical URL) so the destination block has real
  // attraction signal instead of an empty array. Falls back to neighborhoods
  // (Place rather than TouristAttraction since they're string names only).
  const touristDestination = seo.cityName
    ? {
        "@context": "https://schema.org",
        "@type": "TouristDestination",
        "@id": `${seo.canonical}#destination`,
        name: seo.locationName,
        description: seo.lede,
        url: seo.canonical,
        inLanguage,
        address: { "@type": "PostalAddress", addressCountry: "MA", addressLocality: seo.cityName },
        containedInPlace: { "@type": "Country", name: "Morocco" },
        touristType: ["Adventure", "Culture", "Family", "Solo", "Luxury"],
        availableLanguage: ["en", "fr", "ar"],
        ...(CITY_GEO[seo.citySlug ?? ""]
          ? { geo: { "@type": "GeoCoordinates", ...CITY_GEO[seo.citySlug ?? ""] } }
          : {}),
        ...(items.length > 0
          ? {
              includesAttraction: items.slice(0, 10).map((it) => ({
                "@type": "TouristAttraction",
                name: it.title,
                url: `${origin}${activityHrefString({
                  lang,
                  citySlug: it.citySlug ?? null,
                  categorySlug: it.categorySlug ?? null,
                  segment: it.segment ?? null,
                  slug: it.slug,
                })}`,
              })),
            }
          : seo.neighborhoods.length > 0
          ? {
              containsPlace: seo.neighborhoods.map((n) => ({
                "@type": "Place",
                name: n,
              })),
            }
          : {}),
      }
    : null;

  const collection = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: seo.title,
    description: seo.description,
    url: seo.canonical,
    inLanguage,
    isPartOf: { "@id": "https://majourneys.com/#website" },
    breadcrumb: { "@id": breadcrumbId },
    mainEntity: { "@id": itemListId },
    about: seo.categoryName
      ? { "@type": "Thing", name: seo.categoryName }
      : undefined,
    spatialCoverage: seo.cityName
      ? { "@type": "Place", name: seo.locationName }
      : undefined,
  };

  // options is reserved for future per-page tweaks (e.g. force a different
  // breadcrumb tail). Accept it now so callers can pass context without
  // schema breaking later.
  void options;

  return [itemList, breadcrumb, faq, touristDestination, collection].filter(Boolean);
}
